import React, { useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Grid from "@mui/material/Grid";
import TransactionService from "../core/service/transaction.service";
import { useAuth } from "../core/context/auth.context";
import { useSocket } from "../core/context/socket.context";
import TransactionFilterInterface from "@homegame/common/dist/interface/filters/transaction-filter.interface";
import { useSocketSubscription } from "../core/hooks/use-web-socket-subscription.hook";
import TransactionItem from "./components/transaction-item.component";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import dayjs from "dayjs";
import TanstackTableComponent from "../core/components/tanstack-table.component";
import { ColumnDef } from "@tanstack/react-table";
import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";

const PAGE_SIZE = 5;

const useStyles = makeStyles(() =>
  createStyles({
    wrap: {
      minWidth: "100%",
      paddingLeft: 0,
      height: "100%",
    },
    table: {},
    title: {
      color: "#47528c",
      fontSize: 15,
      fontWeight: 500,
      textShadow: "none",
      paddingRight: 10,
      "& span": {
        fontWeight: 500,
      },
    },
    heading: {
      fontSize: 10,
      width: "100%",
    },
    value: {
      width: "100%",
      fontSize: 13,
      color: "#47528c",
      textShadow: "none",
      fontWeight: 400,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
      },
    },
    date: {
      width: "100%",
      fontSize: 12,
      color: "#47528c",
      textShadow: "none",
      fontWeight: 400,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
      },
    },
  }),
);

interface TransactionsListProps {
  pageSize?: number;
  showTableName?: boolean;
  showStatus?: boolean;
  height?: number | string;
  onActionDone?: () => void;
  filters?: TransactionFilterInterface;
}

export default function TransactionsListComponent({
  pageSize = PAGE_SIZE,
  showTableName = false,
  showStatus = false,
  height,
  filters = {},
  onActionDone,
}: TransactionsListProps) {
  const classes = useStyles({});
  const transactionsService = new TransactionService();

  const { user } = useAuth();
  const { transactionSocket } = useSocket();

  const [refreshTrigger, setRefreshTrigger] = useState(1);

  const tableFilters: TransactionFilterInterface = {
    ...filters,
    take: pageSize,
    skip: 0,
  };

  useEffect(() => {
    if (filters) {
      setRefreshTrigger(refreshTrigger + 1);
    }
  }, [filters]);

  const handleBtnAction = () => {
    setRefreshTrigger(refreshTrigger + 1);
    if (onActionDone) onActionDone();
  };

  const columns: ColumnDef<TransactionInterface>[] = [
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => {
        const transaction: any = info.row.original;
        return (
          <TransactionItem
            transaction={transaction}
            userId={tableFilters.userId}
            showTableName={showTableName}
            showStatus={
              showStatus ||
              !(
                transaction.type === TransactionType.PAYMENT &&
                transaction.fromUserId === tableFilters.userId
              )
            }
            onActionDone={handleBtnAction}
          />
        );
      },
    },
  ];

  const doRefresh = (data: any) => {
    if (
      [data.transaction.fromUserId, data.transaction.toUserId].includes(
        user?.id,
      )
    ) {
      setRefreshTrigger(refreshTrigger + 1);
    }
  };

  useSocketSubscription(transactionSocket?.connect(), [
    {
      eventName: "transactionUpdated",
      callback: doRefresh,
    },
    {
      eventName: "transactionCreated",
      callback: doRefresh,
    },
  ]);

  return (
    <Grid item xs={12} className={classes.wrap}>
      <TanstackTableComponent<TransactionInterface>
        columns={columns}
        listMethod={transactionsService.list.bind(transactionsService)}
        filters={tableFilters}
        height={height}
        className={classes.table}
        getRowId={(row: any) => row.id!}
        hideHeader
        groupByDateKey="createdAt"
        disableAutoGroupByDate={false}
        formatGroupLabel={(rawDate) => dayjs(rawDate).format("MMMM DD, YYYY")}
      />
    </Grid>
  );
}
